import { Button, Collapse, Grid, IconButton, Popover, TextField } from '@mui/material'
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon'
import { AddCircle, ExpandLess, ExpandMore, FilterList } from '@mui/icons-material'
import { observer } from 'mobx-react'
import * as React from 'react'
import ESDataTable, { IESDataTableProps } from './ESDataTable'
import ESDataTableFilterDialog from './ESDataTableFilterDialog/ESDataTableFilterDialog'
import ITableOptions from './ITableOptions'

export interface IESDataTableWithHeaderProps {
  isAddButtonDisabled?: boolean
  elevation?: number
  enableMultiSelect?: boolean
  enableSearch?: boolean
  enableShowInactives?: boolean
  enableFilters?: boolean
  filterComponents?: JSX.Element
  headerContents?: JSX.Element
  headerPadding?: string | number
  addButtonOnClick?: () => void
  padding?: string | number
  additionalHeaderFeatures?: JSX.Element[]
  title?: string
  icon?: React.ComponentType<SvgIconProps>
  rowsPerPage?: number
  rowCellStyleFunction?: (data: any, columnFieldName?: string) => React.CSSProperties
  isCollapsible?: boolean
  expanded?: boolean
  verifiedIcon?: JSX.Element
  selectable?: boolean
}

@observer
class ESDataTableWithHeader extends React.Component<
  IESDataTableWithHeaderProps & IESDataTableProps
> {
  public state = {
    anchorEl: null,
    isExpanded: this.props.expanded,
    showFilters: false,
  }
  private handleFiltersClick = (event: React.MouseEvent) => {
    event.stopPropagation()
    let anchorEl = null
    if (!this.state.showFilters) {
      anchorEl = event.currentTarget
    }
    this.setState({ anchorEl, showFilters: !this.state.showFilters })
  }
  public render() {
    const {
      additionalHeaderFeatures = [],
      dataTableManager,
      elevation,
      enableMultiSelect,
      enableSearch,
      enableShowInactives,
      enableFilters = true,
      filterComponents,
      headerContents,
      headerPadding,
      isCollapsible,
      padding,
      addButtonOnClick,
      isAddButtonDisabled = false,
      title,
      rowCellStyleFunction,
      ...rest
    } = this.props
    const search = enableSearch && (
      <TextField
        sx={{ marginBottom: '5px' }}
        label="Search"
        margin="none"
        name="filter"
        onChange={this.onSearchChanged}
        value={dataTableManager.tableOptions.filter}
      />
    )

    const addButtonComponent = addButtonOnClick && (
      <Grid item>
        <AddCircle
          color={isAddButtonDisabled ? 'disabled' : 'primary'}
          onClick={isAddButtonDisabled ? undefined : addButtonOnClick}
          style={{
            fontSize: '30px',
            marginRight: '8px',
            cursor: isAddButtonDisabled ? 'auto' : 'pointer',
          }}
        />
      </Grid>
    )

    function setUpAdditionalHeaderFeatures() {
      return additionalHeaderFeatures.map((feature: JSX.Element) => (
        <Grid item>{feature}</Grid>
      ))
    }

    const onArrowClick = (event: React.MouseEvent) => {
      event.stopPropagation()
      this.setState({ isExpanded: !this.state.isExpanded })
    }

    const arrowButton = (
      <>
        {this.state.isExpanded ? (
          <IconButton onClick={onArrowClick}>
            <ExpandLess />
          </IconButton>
        ) : (
          <IconButton onClick={onArrowClick}>
            <ExpandMore />
          </IconButton>
        )}
      </>
    )

    return (
      <Grid
        sx={{
          padding: '8px 24px',
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
        container
        direction="column"
      >
        <Grid
          alignItems="center"
          sx={{ paddingBottom: '10px' }}
          style={{ padding: headerPadding }}
          container
          item
          justifyContent="space-between"
          wrap="nowrap"
          spacing={1}
        >
          <Grid container item alignItems="center" spacing={2}>
            {this.tableHeader()}
            <Grid item>{search}</Grid>
            {setUpAdditionalHeaderFeatures()}
          </Grid>
          {enableFilters && (
            <>
              {isCollapsible && <Grid item> {arrowButton} </Grid>}
              <Grid item>
                <Button
                  onClick={this.handleFiltersClick}
                  style={{ textTransform: 'none' }}
                  size="small"
                >
                  <FilterList style={{ fontSize: '32px' }} color="secondary" />
                </Button>
                <Popover
                  open={this.state.showFilters}
                  transformOrigin={{
                    horizontal: 'center',
                    vertical: 'top',
                  }}
                  anchorEl={this.state.anchorEl}
                >
                  <ESDataTableFilterDialog
                    dataTableManager={dataTableManager}
                    filterComponents={filterComponents}
                    enableShowInactives={enableShowInactives}
                    updateTableOptions={this.updateTableOptions}
                    handleFilterClickAway={this.handleFilterClickAway}
                  />
                </Popover>
              </Grid>
            </>
          )}

          {addButtonComponent}
        </Grid>
        {headerContents ? (
          <Grid container item>
            {headerContents}
          </Grid>
        ) : (
          <></>
        )}
        {this.props.isCollapsible ? (
          <Collapse in={this.state.isExpanded}>
            <ESDataTable
              elevation={elevation}
              enableMultiSelect={enableMultiSelect}
              {...rest}
              dataTableManager={dataTableManager}
              rowCellStyleFunction={rowCellStyleFunction}
            />
          </Collapse>
        ) : (
          <ESDataTable
            elevation={elevation}
            enableMultiSelect={enableMultiSelect}
            {...rest}
            dataTableManager={dataTableManager}
            rowCellStyleFunction={rowCellStyleFunction}
            verifiedIcon={this.props.verifiedIcon}
            selectable={this.props.selectable}
            title={title}
          />
        )}
      </Grid>
    )
  }
  private tableHeader(): React.ReactNode {
    const title = this.props.title
    const icon = this.props.icon

    return (
      <>
        {icon && (
          <Grid item>
            <SvgIcon component={icon} style={{ fontSize: '32px' }} color="secondary" />
          </Grid>
        )}
        {title && (
          <Grid item sx={{ fontSize: '20px', paddingTop: '4px' }}>
            {title}
          </Grid>
        )}
      </>
    )
  }

  private handleFilterClickAway = () => {
    this.setState({
      showFilters: false,
    })
  }

  private onSearchChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.updateTableOptions({ filter: value, page: 1 })
  }

  private updateTableOptions = (tableOptions: ITableOptions) => {
    const options = {
      ...this.props.dataTableManager.tableOptions,
      ...tableOptions,
    }
    this.props.dataTableManager.updateTableOptions(options)
  }
}

export default ESDataTableWithHeader
